import type { ComponentType } from "react";
import { CASE_STUDIES_ENABLED } from "@/config/features";
import Hero from "@/components/Hero";
import BusinessResultStrip from "@/components/beedigit/BusinessResultStrip";
import CustomerLogos from "@/components/beedigit/CustomerLogos";
import {
  ContinuousImprovementFoundation, LeanWasteTable, DataToKaizenFlow, LeanSixSigmaToolkit, OpExTeamsRoles, LeanKPIDashboards,
  DailyManagementSystem, SixSigmaDMAIC, TPMSupport, StandardWorkDigital5S, LeanFinalCTA,
} from "@/components/beedigit/LeanSections";
import ProblemGap from "@/components/beedigit/ProblemGap";
import SolutionOverview from "@/components/beedigit/SolutionOverview";
import MarketPositioning from "@/components/beedigit/MarketPositioning";
import StartSmallScaleBig from "@/components/beedigit/StartSmallScaleBig";
import IndustryTemplates from "@/components/beedigit/IndustryTemplates";
import IndustryProcessPreview from "@/components/beedigit/IndustryProcessPreview";
import ModulesGrid from "@/components/beedigit/ModulesGrid";
import HardwareBlock from "@/components/beedigit/HardwareBlock";
import IntegrationsBlock from "@/components/beedigit/IntegrationsBlock";
import BusinessOutcomes from "@/components/beedigit/BusinessOutcomes";
import OnboardingTimeline from "@/components/beedigit/OnboardingTimeline";
import SecurityEnterprise from "@/components/beedigit/SecurityEnterprise";
import CaseStudiesPreview from "@/components/beedigit/CaseStudiesPreview";
import RoleBasedValue from "@/components/beedigit/RoleBasedValue";
import HomeFAQ from "@/components/beedigit/HomeFAQ";
import Contact from "@/components/Contact";
import type { PageBlock } from "@/lib/pages/schema";
import { hasCustomStyle, nativeStyleClasses } from "./BlockRenderer";

const ContactDemo = () => <Contact id="demo" />;
const CaseStudiesMaybe = () => (CASE_STUDIES_ENABLED ? <CaseStudiesPreview /> : null);

/** Home sections in built-in order; ids match HOME_SECTION_IDS and the generated snapshot. */
export const HOME_SECTIONS: [string, ComponentType][] = [
  ["hero", Hero], ["business-result-strip", BusinessResultStrip], ["customer-logos", CustomerLogos],
  ["ci-foundation", ContinuousImprovementFoundation], ["lean-waste-table", LeanWasteTable], ["data-to-kaizen", DataToKaizenFlow],
  ["problem-gap", ProblemGap], ["solution-overview", SolutionOverview], ["market-positioning", MarketPositioning],
  ["start-small", StartSmallScaleBig], ["industry-templates", IndustryTemplates], ["industry-process-preview", IndustryProcessPreview],
  ["modules-grid", ModulesGrid], ["hardware-block", HardwareBlock], ["integrations-block", IntegrationsBlock],
  ["business-outcomes", BusinessOutcomes], ["lean-toolkit", LeanSixSigmaToolkit], ["lean-kpi-dashboards", LeanKPIDashboards],
  ["daily-management", DailyManagementSystem], ["six-sigma-dmaic", SixSigmaDMAIC], ["tpm-support", TPMSupport],
  ["standard-work-5s", StandardWorkDigital5S], ["onboarding-timeline", OnboardingTimeline], ["security-enterprise", SecurityEnterprise],
  ["case-studies-preview", CaseStudiesMaybe], ["role-based-value", RoleBasedValue], ["opex-teams", OpExTeamsRoles],
  ["home-faq", HomeFAQ], ["lean-final-cta", LeanFinalCTA], ["contact-form", ContactDemo],
];
const HOME_MAP = new Map(HOME_SECTIONS);

const NATIVE: Record<string, ComponentType> = {
  contact_form: ContactDemo, customer_logos: CustomerLogos, modules_grid: ModulesGrid, hardware_block: HardwareBlock, integrations_block: IntegrationsBlock,
};

export function renderNative(block: Extract<PageBlock, { type: "native" }>) {
  const c = block.props.component;
  if (c === "page_body") return null; // rendered by the page itself between its slots
  const Comp = c.startsWith("home:") ? HOME_MAP.get(c.slice(5)) : NATIVE[c];
  if (!Comp) return null;
  if (!hasCustomStyle(block.style)) return <Comp key={block.id} />;
  return <div key={block.id} data-block-id={block.id} className={nativeStyleClasses(block.style)}><Comp /></div>;
}
